import React, { useMemo, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  useServicesList,
  useServiceStatusMutation,
  useServiceDeleteMutation,
} from '@/features/services/hooks'
import type { Service } from '@/features/services/types'
import { ServiceEditor } from '../ServiceEditor'
import { ServicesHeader } from './ServicesHeader'
import { ServicesStatsGrid } from './ServicesStatsGrid'
import { ServicesDirectoryCard } from './ServicesDirectoryCard'
import { ServicesQuickActions } from './ServicesQuickActions'
import { ServicesTable } from './ServicesTable'
import { ServicesAdvancedFiltersDialog } from './ServicesAdvancedFiltersDialog'
import { ServicesPreviewDialog } from './ServicesPreviewDialog'
import { ServicesLoadingState } from './ServicesLoadingState'
import { ServicesErrorState } from './ServicesErrorState'

export function ServicesManagementContainer() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [currentView, setCurrentView] = useState<'list' | 'editor'>('list')
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [previewService, setPreviewService] = useState<Service | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [featuredFilter, setFeaturedFilter] = useState<'all' | 'featured' | 'standard'>('all')
  const [footerFilter, setFooterFilter] = useState<'all' | 'footer' | 'non-footer'>('all')
  const [serviceAreasOnly, setServiceAreasOnly] = useState(false)
  const [seoAuditMode, setSeoAuditMode] = useState(false)

  const { data: services = [], isLoading, error } = useServicesList()
  const updateStatusMutation = useServiceStatusMutation()
  const deleteMutation = useServiceDeleteMutation()

  const filteredServices = useMemo(() => {
    const hasSeoGap = (service: Service) => {
      const hasMeta = Boolean(service.meta_title && service.meta_description)
      const hasHero = Boolean(service.hero_image)
      const hasSubstantiveContent = Boolean(service.content && service.content.length >= 300)
      return !(hasMeta && hasHero && hasSubstantiveContent)
    }

    return services.filter((service) => {
      const haystack = `${service.title} ${service.description}`.toLowerCase()
      const matchesSearch = haystack.includes(searchTerm.toLowerCase())
      const matchesStatus = statusFilter === 'all' || service.status === statusFilter
      const matchesFeatured =
        featuredFilter === 'all' ||
        (featuredFilter === 'featured' ? service.is_featured : !service.is_featured)
      const matchesFooter =
        footerFilter === 'all' ||
        (footerFilter === 'footer' ? Boolean(service.is_footer_service) : !service.is_footer_service)
      const matchesServiceAreas = !serviceAreasOnly || Boolean(service.service_areas && service.service_areas.length > 0)
      const matchesSeoAudit = !seoAuditMode || hasSeoGap(service)
      return matchesSearch && matchesStatus && matchesFeatured && matchesFooter && matchesServiceAreas && matchesSeoAudit
    })
  }, [services, searchTerm, statusFilter, featuredFilter, footerFilter, serviceAreasOnly, seoAuditMode])

  const stats = useMemo(
    () => ({
      total: services.length,
      published: services.filter((service) => service.status === 'published').length,
      draft: services.filter((service) => service.status === 'draft').length,
      featured: services.filter((service) => service.is_featured).length,
    }),
    [services]
  )

  const advancedFilterCount = useMemo(
    () =>
      [featuredFilter !== 'all', footerFilter !== 'all', serviceAreasOnly, seoAuditMode].filter(Boolean).length,
    [featuredFilter, footerFilter, serviceAreasOnly, seoAuditMode]
  )

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      deleteMutation.mutate(id)
    }
  }

  const handleStatusChange = (id: string, newStatus: Service['status']) => {
    updateStatusMutation.mutate({ id, status: newStatus })
  }

  const handleNewService = () => {
    setSelectedService(null)
    setCurrentView('editor')
  }

  const handleEditService = (service: Service) => {
    setSelectedService(service)
    setCurrentView('editor')
  }

  const handleViewService = (service: Service) => {
    setPreviewService(service)
    setIsPreviewOpen(true)
  }

  const handleEditorSave = () => {
    setCurrentView('list')
    setSelectedService(null)
  }

  const handleEditorCancel = () => {
    setCurrentView('list')
    setSelectedService(null)
  }

  const handleEditorPreview = (service: Service) => {
    setPreviewService(service)
    setIsPreviewOpen(true)
  }

  const resetAdvancedFilters = () => {
    setFeaturedFilter('all')
    setFooterFilter('all')
    setServiceAreasOnly(false)
    setSeoAuditMode(false)
  }

  if (isLoading) {
    return <ServicesLoadingState />
  }

  if (error) {
    return <ServicesErrorState />
  }

  if (currentView === 'editor') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleEditorCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Button>
        </div>
        <ServiceEditor
          service={selectedService || undefined}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onPreview={handleEditorPreview}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ServicesHeader onAddService={handleNewService} />
      <ServicesStatsGrid stats={stats} />
      <ServicesDirectoryCard
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        advancedFilterCount={advancedFilterCount}
        featuredFilter={featuredFilter}
        footerFilter={footerFilter}
        serviceAreasOnly={serviceAreasOnly}
        seoAuditMode={seoAuditMode}
        onOpenAdvancedFilters={() => setShowAdvancedFilters(true)}
        onFeaturedFilterChange={setFeaturedFilter}
        onFooterFilterChange={setFooterFilter}
        onServiceAreasToggle={setServiceAreasOnly}
        onSeoAuditToggle={setSeoAuditMode}
        onResetAdvancedFilters={resetAdvancedFilters}
      />
      <ServicesTable
        services={filteredServices}
        onView={handleViewService}
        onEdit={handleEditService}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        deletePending={deleteMutation.isPending}
      />
      <ServicesQuickActions
        seoAuditMode={seoAuditMode}
        onEnableSeoAudit={() => {
          setSeoAuditMode(true)
          setShowAdvancedFilters(true)
        }}
        serviceAreasOnly={serviceAreasOnly}
        onToggleServiceAreasOnly={() => setServiceAreasOnly((prev) => !prev)}
        statusFilter={statusFilter}
        onSetStatusFilter={setStatusFilter}
        onAddService={handleNewService}
      />
      <ServicesAdvancedFiltersDialog
        open={showAdvancedFilters}
        onOpenChange={setShowAdvancedFilters}
        featuredFilter={featuredFilter}
        onFeaturedFilterChange={setFeaturedFilter}
        footerFilter={footerFilter}
        onFooterFilterChange={setFooterFilter}
        serviceAreasOnly={serviceAreasOnly}
        onServiceAreasToggle={setServiceAreasOnly}
        seoAuditMode={seoAuditMode}
        onSeoAuditToggle={setSeoAuditMode}
        onDone={() => setShowAdvancedFilters(false)}
      />
      <ServicesPreviewDialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen} service={previewService} />
    </div>
  )
}
